import { featureCollection, intersect as turfIntersect } from '@turf/turf';
import { type Feature } from 'ol';
import { type Geometry, type Polygon } from 'ol/geom';
import type VectorSource from 'ol/source/Vector';
import { geoJSONEpsg3857 } from '../../../mapStyleUtils.ts';
import { getBufferedFeatureJSON } from './getBufferedFeatureJSON.ts';
import { getRoundedFeatureArea } from './getRoundedFeatureArea.ts';

export interface IComputeFeaturesIntersectionOptions {
  /** Distanza di buffer (metri) per geometrie non-poligonali (Point, LineString) */
  bufferDistance?: number;
}

/**
 * Calcola l'intersezione tra le geometrie disegnate e quelle presenti nel layer.
 *
 * Per ogni feature disegnata nel `featuresSource`, ne ottiene la rappresentazione
 * GeoJSON poligonale (applicando un buffer per Point/LineString, vedi
 * {@link getBufferedFeatureJSON}) e la confronta con le geometrie del
 * `intersectionSource` usando Turf.js.
 *
 * Per ogni coppia che si sovrappone, invoca `onFeatureFound` con la feature
 * del layer, il poligono di intersezione risultante, l'area calcolata e la
 * feature disegnata originale.
 */
export function computeFeaturesIntersection(
  featuresSource: VectorSource,
  intersectionSource: VectorSource,
  onFeatureFound: (
    intersectable: Feature<Geometry>,
    feature: Feature<Polygon>,
    area: number,
    drawnFeature: Feature<Geometry>,
  ) => void,
  options?: IComputeFeaturesIntersectionOptions,
) {
  const features = featuresSource.getFeatures();
  const featuresExtent = featuresSource.getExtent();

  // Iteriamo sulle feature disegnate
  for (const feature of features) {
    const featureJson = getBufferedFeatureJSON(
      feature,
      options?.bufferDistance,
    );
    if (!featureJson) continue;

    // Iteriamo sulle feature del layer di intersezione che intersecano
    // la geometria disegnata. Utilizziamo la funzione
    // `forEachFeatureIntersectingExtent` per evitare di caricare tutte le
    // geometrie del layer di intersezione.
    intersectionSource.forEachFeatureIntersectingExtent(
      featuresExtent,
      intersectable => {
        const intersectableJson = getBufferedFeatureJSON(intersectable);
        if (!intersectableJson) return;

        const intersections = turfIntersect(
          featureCollection([featureJson, intersectableJson]),
        );

        // Se non ci sono intersezioni, passiamo alla prossima feature
        if (!intersections) return;

        // Le intersezioni sono delle geometrie di tipo Polygon restituite
        // da Turf.js. Le convertiamo in feature OpenLayers e le aggiungiamo
        // alla lista delle intersezioni.
        const intersectionFeatures =
          geoJSONEpsg3857.readFeatures(intersections);
        for (const intersectionFeature of intersectionFeatures) {
          // Nessun id sulla feature: verrebbe serializzato nel GeoJSON
          // persistito (`buildIntersectionItems`) rendendo il valore diverso a
          // ogni ricalcolo a parità di geometria. L'id non viene mai riletto.
          const area = getRoundedFeatureArea(intersectionFeature);

          onFeatureFound(
            intersectable,
            intersectionFeature as Feature<Polygon>,
            area,
            feature,
          );
        }
      },
    );
  }
}
